<template>
  <div class="box is-centered wrapper-auto-invoice-details">
    <div class="title-container is-flex">
      <h1 class="title is-centered is-4 has-text-primary title-auto-invoice">
        Detalles de Autofactura
      </h1>

      <div class="field is-centered">
        <input
          id="advanced"
          v-model="advanced"
          name="advanced"
          class="is-checkradio"
          :class="{ 'is-success': advanced }"
          type="checkbox"
          @change="onChangeAdvanced"
        >
        <label
          class="is-centered"
          for="advanced"
        >
          Opciones Avanzadas
        </label>
      </div>
    </div>

    <div class="columns is-multiline">
      <div
        v-if="!isTradeTransfer"
        class="column"
        :class="{ 'is-12': !inline, 'is-4': inline }"
      >
        <AppSelectSearch
          id="voucherPaymentType"
          name="voucherPaymentType"
          label="Forma de pago"
          placeholder="Selecciona la forma de pago"
          :options="paymentTypeCatalog"
          :value="paymentTypeSelectedValue"
          :disabled="isLoading || disabled"
          @input="onChange"
          @change="onChange"
        />
      </div>

      <div
        class="column"
        :class="{ 'is-12': !inline, 'is-4': inline }"
      >
        <CustomerSearcherByEmail
          :disabled="isLoading || disabled"
          :value="email"
          required
          @select="$emit('customer', $event)"
        />
      </div>

      <div
        class="column"
        :class="{ 'is-12': !inline, 'is-4': inline }"
      >
        <CustomerSearcherByName
          :disabled="isLoading || disabled"
          :value="name"
          required
          @select="$emit('customer', $event)"
        />
      </div>

      <div
        v-show="advanced"
        class="column"
        :class="{ 'is-12': !inline, 'is-4': inline }"
      >
        <AppInputField
          id="voucherDate"
          name="voucherDate"
          label="Fecha y hora de expedición*:"
          type="dateTime-local"
          :value="voucherDateToInput"
          :min="minDateTime"
          :max="maxDateTime"
          left-icon="calendar"
          disabled
        />
      </div>
      <template v-if="advanced">
        <div
          v-if="false"
          class="column"
          :class="{ 'is-12': !inline, 'is-4': inline }"
        >
          <AppInputField
            id="voucherExpeditionPlace"
            name="voucherExpeditionPlace"
            label="Código postal*:"
            placeholder="02719"
            :value="voucherExpeditionPlace"
            :disabled="isLoading || disabled"
            :required="true"
            @input="onChange"
            @change="onChange"
          />
        </div>

        <div
          class="column"
          :class="{ 'is-12': !inline, 'is-4': inline }"
        >
          <AppSelectSearch
            id="voucherCurrency"
            name="voucherCurrency"
            label="Moneda*:"
            placeholder="Selecciona una moneda"
            :options="currencyCatalog"
            :value="currencySelectedValue"
            :disabled="isLoading || disabled"
            @input="onChangeCurrency"
            @change="onChangeCurrency"
          />
        </div>
        <div
          v-show="otherCurrency"
          class="column"
          :class="{ 'is-12': !inline, 'is-4': inline }"
        >
          <AppInputField
            id="voucherExchangeRate"
            name="voucherExchangeRate"
            label="Tipo de cambio:"
            placeholder="0.00"
            :value="voucherExchangeRate"
            :disabled="isLoading || disabled"
            @input="onChange"
            @change="onChange"
          />
        </div>

        <div
          v-if="!isTradeTransfer"
          class="column"
          :class="{ 'is-12': !inline, 'is-4': inline }"
        >
          <AppSelectField
            id="voucherPaymentMethod"
            name="voucherPaymentMethod"
            label="Metodo de pago:"
            :options="paymentMethodCatalog"
            :value="voucherPaymentMethod"
            :disabled="isLoading || disabled"
            @input="onChange"
            @change="onChange"
          />
        </div>

        <template v-if="voucherPaymentMethod === 'PPD'">
          <div
            class="column"
            :class="{ 'is-12': !inline, 'is-4': inline }"
          >
            <BField
              label="Fecha de vencimiento"
              label-for="expirationDate"
            >
              <BDatepicker
                id="expirationDate"
                :value="expirationDate"
                :date-parser="dateParser"
                :disabled="isLoading || disabled"
                v-bind="calendarProps"
                icon="calendar-today"
                @input="onChangeExpiration('operation.expirationDate', $event)"
                @change="onChangeExpiration('operation.expirationDate', $event)"
              />
            </BField>
          </div>
        </template>

        <div
          class="column"
          :class="{ 'is-12': !inline, 'is-4': inline }"
        >
          <AppSelectField
            id="voucherType"
            name="voucherType"
            label="Tipo de comprobante:"
            :options="voucherTypeCatalog"
            :value="voucherType"
            disabled
            @input="onChange"
            @change="onChange"
          />
        </div>

        <div class="column is-6-mobile is-3">
          <AppInputField
            id="voucherSeries"
            name="voucherSeries"
            label="Serie:"
            :pattern="pattern"
            :value="voucherSeries"
            :disabled="isLoading || disabled"
            @input="onChange"
            @change="onChange"
          />
        </div>

        <div class="column is-6-mobile is-4">
          <AppInputField
            id="voucherConsecutive"
            name="voucherConsecutive"
            label="Folio:"
            type="number"
            min="1"
            :value="voucherConsecutive"
            :disabled="isLoading || disabled"
            @input="onChange"
            @change="onChange"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import moment from 'moment';
import {
  currency,
  wayTopay,
  payMethod,
} from '@machete/common-catalogs';

const arrayToOptions = array => array.map(
  ({ key, value }) => ({
    value: key,
    label: `${key} - ${value}`,
  }),
);

export default {
  name: 'VoucherDetails',
  components: {
    AppSelectField: () => import(/* webpackChunkName: "app-select-field" */ '@/components/AppSelectField.vue'),
    AppSelectSearch: () => import(/* webpackChunkName: "app-select-search" */ '@/components/AppSelectSearch.vue'),
    AppInputField: () => import(/* webpackChunkName: "app-input-field" */ '@/components/AppInputField.vue'),
    CustomerSearcherByEmail: () => import(/* webpackChunkName: "customer-searcher-by-email" */ '@/components/Admin/Customer/SearcherByEmail.vue'),
    CustomerSearcherByName: () => import(/* webpackChunkName: "customer-searcher-by-name" */ '@/components/Admin/Customer/SearcherByName.vue'),
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isTradeTransfer: {
      type: Boolean,
      default: false,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    pattern: {
      type: String,
      default: '^[a-zA-Z0-9]{0,25}$',
    },
  },
  data() {
    const voucherType = [
      { key: 'I', value: 'Ingreso' },
      { key: 'N', value: 'Nómina' },
      { key: 'T', value: 'Traslado' },
      { key: 'E', value: 'Egreso' },
    ];

    return {
      advanced: false,
      advancedClicked: false,
      currencyCatalog: arrayToOptions(currency),
      paymentMethodCatalog: arrayToOptions(payMethod),
      paymentTypeCatalog: [],
      voucherTypeCatalog: arrayToOptions(voucherType),
      minDateTime: moment().subtract(3, 'days').format('YYYY-MM-DDTHH:mm'),
      maxDateTime: moment().format('YYYY-MM-DDTHH:mm'),
      otherCurrency: false,
      calendarProps: {
        placeholder: 'DD/MM/AAAA',
        monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
        dayNames: ['D', 'L', 'Ma', 'Mi', 'J', 'V', 'S'],
        editable: true,
        dateFormatter: date => moment(date).format('DD/MM/YYYY'),
      },
    };
  },
  computed: {
    ...mapState({
      voucherDate: state => state.invoice.voucherDate,
      voucherExpeditionPlace: state => state.invoice.voucherExpeditionPlace,
      voucherConsecutive: state => state.invoice.voucherConsecutive,
      voucherCurrency: state => state.invoice.voucherCurrency,
      voucherExchangeRate: state => state.invoice.voucherExchangeRate,
      voucherSeries: state => state.invoice.voucherSeries,
      voucherPaymentType: state => state.invoice.voucherPaymentType,
      voucherPaymentMethod: state => state.invoice.voucherPaymentMethod,
      voucherType: state => state.invoice.voucherType,
      email: state => state.users.email,
      name: state => state.users.name,
      balance: state => state.invoice.operation.balance,
      expirationDate: state => state.invoice.operation.expirationDate,
    }),
    ...mapGetters({
      voucherDateToInput: 'invoice/voucherDateToInput',
    }),
    currencySelectedValue() {
      return this.currencyCatalog.find(curr => curr.value === this.voucherCurrency);
    },
    paymentTypeSelectedValue() {
      return this.paymentTypeCatalog.find(curr => curr.value === this.voucherPaymentType);
    },
    expirationDate() {
      return new Date(moment(this.$store.state.invoice.operation.expirationDate).format('MM/DD/YYYY'));
    },
  },
  watch: {
    voucherTotal(value) {
      this.onChange({ name: 'voucherTotal', value });
    },
    voucherSubTotal(value) {
      this.onChange({ name: 'voucherSubTotal', value });
    },
    voucherPaymentMethod(value) {
      if (value === 'PPD') {
        wayTopay.push({ key: '99', value: 'Por definir' });
      } else {
        wayTopay.pop();
      }
      this.paymentTypeCatalog = arrayToOptions(wayTopay);
    },
  },
  async beforeMount() {
    wayTopay.pop();
    this.paymentTypeCatalog = arrayToOptions(wayTopay);
  },
  methods: {
    ...mapMutations({
      setVoucherValue: 'invoice/setValue',
      setUserValue: 'users/setValue',
    }),
    dateParser: date => moment(date).toDate(),
    onChange(event) {
      const { name: accessor, value } = event.target;
      this.setVoucherValue({ accessor, value });
    },
    onChangeCurrency(event) {
      const { name: accessor, value } = event.target;
      this.setVoucherValue({ accessor, value });
      this.otherCurrency = value !== 'MXN';
    },
    onChangeUser(event) {
      const { name: accessor, value } = event.target;
      this.setUserValue({ accessor, value });
    },
    onChangeAdvanced() {
      if (!this.advancedClicked) {
        this.advancedClicked = true;
        window.FMApi.manualTriggerExperiment();
      }
    },
    onChangeExpiration(name, event) {
      this.setVoucherValue({ accessor: name, value: moment(event).toDate() });
    },
  },
};
</script>

<style scoped>
.box {
  flex-direction: column;
}

.detail-text {
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.2);
  padding: 5px 20px;
  border-radius: 5px;
  color:inherit;
  font-weight: bold;
  float: left;
  clear: both;
}

.float-left {
  float: left;
}

.label.is-centered::before {
  top: inherit;
}

.title {
  margin: 0;
}

.is-checkradio[type="checkbox"] + label::after,
.is-checkradio[type="checkbox"] + label:after {
  width: 0.4rem;
  height: .95rem;
  top: 0.405rem;
  left: 0.6rem;
}

.is-checkradio[type="radio"] + label::before,
.is-checkradio[type="radio"] + label:before,
.is-checkradio[type="checkbox"] + label::before,
.is-checkradio[type="checkbox"] + label:before {
  top: unset;
}

.title-container {
  width: 100%;
  align-items: center;
  padding-bottom: .3rem;
  justify-content: space-between;
}

.columns {
  width: 100%;
}
</style>
